<template>
  <v-container fluid>
    <v-data-table
      v-model="selectedUsers"
      :headers="headers"
      :items="all"
      :search="search"
      :loading="isLoading"
      :show-select="!isLoading"
      :sort-by="['fullName']"
      :footer-props="{
        itemsPerPageOptions: [30, 50, -1],
      }"
      no-data-text="No users found."
      @click:row="onRowClick"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>{{ title }}</v-toolbar-title>
          <v-row justify="end">
            <v-spacer></v-spacer>

            <div v-if="showSelectionOptions" class="text-right">
              <v-slide-y-transition leave-absolute hide-on-leave>
                <ConfirmDialog
                  :title="confirmDeleteTitle"
                  :message="confirmMultiDeleteMessage"
                  type="delete"
                  ok-button-text="Delete"
                  @confirm="doDeleteSelectedUsers"
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      rounded
                      color="red darken-1 white--text"
                      v-on="on"
                    >
                      Delete
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                </ConfirmDialog>
              </v-slide-y-transition>
            </div>
            <div v-else style="max-width: 200px;" class="pt-6">
              <v-slide-y-reverse-transition leave-absolute hide-on-leave>
                <v-text-field
                  key="1"
                  v-model="search"
                  placeholder="Search"
                  append-icon="mdi-magnify"
                >
                </v-text-field>
              </v-slide-y-reverse-transition>
            </div>
          </v-row>
        </v-toolbar>
      </template>
      <template v-slot:item.fullName="{ item }">
        {{ item.lastName }}, {{ item.firstName }}
      </template>
      <template v-slot:item.isOptedIn="{ item }">
        <div v-if="item.isOptedIn" class="green--text">
          <v-icon color="green">mdi-check</v-icon> Yes
        </div>
        <div v-else class="red--text">
          <v-icon color="red">mdi-cancel</v-icon> No
        </div>
      </template>
    </v-data-table>
    <router-view></router-view>
  </v-container>
</template>

<script>
import Logger from "@/services/Logger";
import ConfirmDialog from "@/components/ConfirmDialog";
import { mapState, mapActions } from "vuex";
import pluralize from "pluralize";

export default {
  name: "UsersGrid",
  components: {
    ConfirmDialog,
  },
  data: () => ({
    search: "",
    isLoading: false,
    selectedUsers: [],
    headers: [
      { text: "Name", value: "fullName" },
      { text: "Email", value: "email" },
      { text: "Phone #", value: "phoneNumber" },
      { text: "Opted In?", value: "isOptedIn", width: "120px" },
      { text: "Roles", value: "commaSeparatedRoles" },
    ],
  }),
  computed: {
    title() {
      if (this.selectedUsers.length > 0) {
        return pluralize("selected user", this.selectedUsers.length, true);
      }
      return "Recipients & Admins";
    },
    showSelectionOptions() {
      return this.selectedUsers.length > 0;
    },
    confirmDeleteTitle() {
      return `Delete ${pluralize("user", this.selectedUsers.length, true)}?`;
    },
    confirmMultiDeleteMessage() {
      const cnt = this.selectedUsers.length;
      return `Are you sure you want to delete ${pluralize(
        "this",
        cnt
      )} ${pluralize("user", cnt, true)}?`;
    },
    ...mapState("users", ["all"]),
  },
  mounted() {
    this.isLoading = true;
    this.loadUsers()
      .then(() => {
        this.selectedUsers = [];

        if (this.value?.length) {
          for (let index = 0; index < this.value.length; index++) {
            const user = this.all.find((u) => u.id === this.value[index]);
            if (user) {
              this.selectedUsers.push(user);
            }
          }
        }
      })
      .finally(() => {
        this.isLoading = false;
      });
  },
  methods: {
    onRowClick(item) {
      this.$router.push({ name: "UserEdit", params: { userId: item.id } });
    },
    doDeleteSelectedUsers() {
      if (!this.selectedUsers.length) {
        return;
      }

      const deleteUserIds = this.selectedUsers.map((u) => u.id);

      this.batchDeleteUsers(deleteUserIds)
        .then(() => {
          this.showSnackbar({
            message: `Deleted ${pluralize(
              "user",
              this.selectedUsers.length,
              true
            )}.`,
          });
          this.selectedUsers = [];
        })
        .catch((err) => {
          Logger.error(err);
          this.showSnackbar({ message: err.message, color: "error" });
        });
    },
    ...mapActions("users", ["loadUsers", "batchDeleteUsers"]),
    ...mapActions("snackbar", ["showSnackbar"]),
  },
};
</script>

<style lang="scss" scoped>
.v-data-table ::v-deep {
  .v-data-table__wrapper {
    table {
      tbody {
        tr {
          td {
            cursor: pointer !important;
          }
        }
      }
    }
  }
}
</style>
